import axios from 'lib/axios'

export interface ICertificationProcess {
  id: number
  subscription_company_id: number
  name: string
  cohort_name: string
  description?: string | null
  registration_open_date?: string | null
  registration_close_date?: string | null
  status: string
  max_capacity?: number | null
  additional_instructions?: string | null
  inventory_name?: string
  client_code?: string
  client_code_active?: boolean
  sessions_count?: string | number
  sessions?: ICertificationSession[]
  created_at?: string
  updated_at?: string
}

export interface ICertificationSession {
  id: number
  certification_process_id: number
  name: string
  session_date: string
  start_time: string
  end_time: string
  timezone: string
  teams_link?: string | null
  description?: string | null
  session_order: number
  created_at?: string
  updated_at?: string
}

export interface ICertificationProcessPayload {
  subscription_company_id: number
  name: string
  cohort_name: string
  description?: string
  registration_open_date?: string
  registration_close_date?: string
  status?: string
  max_capacity?: number | null
  additional_instructions?: string
}

export interface ICertificationSessionPayload {
  name: string
  session_date: string
  start_time: string
  end_time: string
  timezone?: string
  teams_link?: string
  description?: string
  session_order: number
}

type IApiResponse<T> = {
  status: 'OK' | 'error'
  data?: T
  message?: string
}

export const getCertificationProcesses = async (
  subscriptionCompanyId: number,
): Promise<ICertificationProcess[]> => {
  const response = await axios.get<IApiResponse<ICertificationProcess[]>>(
    `/certifications?subscription_company_id=${subscriptionCompanyId}`,
  )

  return response.data.data || []
}

export const getCertificationProcessById = async (
  id: number,
): Promise<ICertificationProcess | null> => {
  try {
    const response = await axios.get<IApiResponse<ICertificationProcess>>(`/certifications/${id}`)
    return response.data.data || null
  } catch (error) {
    return null
  }
}

export const createCertificationProcess = async (
  payload: ICertificationProcessPayload,
): Promise<ICertificationProcess> => {
  const response = await axios.post<IApiResponse<ICertificationProcess>>('/certifications', payload)
  return response.data.data as ICertificationProcess
}

export const updateCertificationProcess = async (
  id: number,
  payload: Partial<ICertificationProcessPayload>,
): Promise<ICertificationProcess> => {
  const response = await axios.put<IApiResponse<ICertificationProcess>>(`/certifications/${id}`, payload)
  return response.data.data as ICertificationProcess
}

export const duplicateCertificationProcess = async (
  id: number,
  payload?: Partial<ICertificationProcessPayload>,
): Promise<ICertificationProcess> => {
  const response = await axios.post<IApiResponse<ICertificationProcess>>(
    `/certifications/${id}/duplicate`,
    payload || {},
  )
  return response.data.data as ICertificationProcess
}

export const createCertificationSession = async (
  processId: number,
  payload: ICertificationSessionPayload,
): Promise<ICertificationSession> => {
  const response = await axios.post<IApiResponse<ICertificationSession>>(
    `/certifications/${processId}/sessions`,
    payload,
  )
  return response.data.data as ICertificationSession
}

export const updateCertificationSession = async (
  sessionId: number,
  payload: Partial<ICertificationSessionPayload>,
): Promise<ICertificationSession> => {
  const response = await axios.put<IApiResponse<ICertificationSession>>(
    `/certifications/sessions/${sessionId}`,
    payload,
  )
  return response.data.data as ICertificationSession
}

export const deleteCertificationSession = async (sessionId: number): Promise<void> => {
  await axios.delete(`/certifications/sessions/${sessionId}`)
}
